/** Dice in a full hand. */
export const DICE_COUNT = 5;
/**
 * Dice you can save (hold) from one turn to the next. Ending a turn keeping all five ends your
 * round instead: five dice are for attacking.
 */
export const MAX_SAVED_DICE = DICE_COUNT - 1;
/** Faces on each die. */
export const DIE_FACES = 6;
/** Turn passes (sequences) each player gets per round before the round resolves. */
export const MAX_SEQUENCES = 3;
/**
 * Clients animate a roll one die at a time, this long each; the server waits that long
 * (times the dice rolled) before a bot's next move so everyone sees the roll land.
 */
export const ROLL_DIE_MS = 1000;
/**
 * Clients keep showing a settled war round (everyone's final dice) this long after the last
 * roll lands, before the outcome; the server holds the next bot move just as long.
 */
export const WAR_REVEAL_MS = 3000;
/**
 * Clients keep showing a ghost roll's dice and outcome this long after the last die lands;
 * the server holds the next bot move just as long.
 */
export const GHOST_REVEAL_MS = 2500;
/**
 * Five of a kind (all dice wild): everyone sees the '5 of a Kind' splash this long; the
 * server holds the next bot move just as long.
 */
export const WILD_SPLASH_MS = 3500;
/**
 * A bot's dice change (its announced move): each step (an arrow flash, and the die's next face)
 * lasts this long. The changed dice move one at a time, left to right.
 */
export const PLAN_STEP_MS = 250;
/** A bot's dice change: the changed dice show this long after the last step before they lock. */
export const PLAN_LOCK_MS = 400;
/** A bot's dice change: the locked dice stay this long before the bot makes its move, so people can take it in. */
export const PLAN_SETTLE_MS = 500;
/** A roll that isn't animated (a bot's, outside a war with a human in it): its dice still appear one at a time, this far apart. */
export const BOT_DIE_STAGGER_MS = 250;
/**
 * An animated roll's dice start rolling `ROLL_DIE_MS` apart and keep rolling; none lands before the
 * last one starts, then they land in order this far apart (the last one still rolls a full
 * `ROLL_DIE_MS`). Up to `DICE_COUNT` dice this ends after `ROLL_DIE_MS` per die, as `rollAnimMs` says.
 */
export const DIE_LAND_STAGGER_MS = 250;
/**
 * How long clients animate a roll: `ROLL_DIE_MS` per die, or `BOT_DIE_STAGGER_MS` per die for a
 * roll that isn't animated (a bot's, outside a war with a human in it: no flashing faces).
 */
export const rollAnimMs = (roll) => roll ? roll.count * (roll.animate !== false ? ROLL_DIE_MS : BOT_DIE_STAGGER_MS) : 0;
/** Motion dice: a throw's launch speed (tray units/s) and spin (rad/s) are capped at these (`DiceToss`). */
export const TOSS_MAX_SPEED = 100;
export const TOSS_MAX_SPIN = 120;
/** Dice rolled in a war round are capped at this many. */
export const MAX_WAR_DICE = 5;
/** Deck colors with card art; seat N plays color N. Caps the table size. */
export const DECK_COLORS = ['blue', 'gray', 'green', 'purple', 'red', 'yellow'];
export const MIN_PLAYERS = 2;
export const MAX_PLAYERS = DECK_COLORS.length;
/**
 * Bot players the host can add in the lobby. Bots have negative player ids.
 * - default: rolls and ends the turn; never holds or attacks.
 * - random:  holds a random set of dice; attacks every card those dice can kill.
 * - average: plays the exact odds.
 * - smart:   plays the odds shaped by its card's personality (the id predates Average: old Smart
 *            bots and saved options get the new Smart).
 */
export const BOT_LEVELS = ['default', 'random', 'average', 'smart'];
/**
 * Host's game options (lobby). Card selection: `choose` lets each player pick (or use their own
 * Random play style); `random` deals every card at random. Ghost rolls: see `Game.ghostRoll`.
 */
export const CARD_SELECTIONS = ['choose', 'random'];
/**
 * A player's own option (Options, kept in the browser): `choose` picks each card by hand, `random`
 * has the game pick one at random whenever a card is needed. Counted per game on the
 * leaderboards: `random` when every card they played that game was picked at random (their
 * Random play style, or a Random Only game dealing it).
 */
export const PLAY_STYLES = ['choose', 'random'];
/**
 * Host's game option: the deck each player starts with. `standard`: the variant's whole hand;
 * `knights`: only its Knights (Kill Quest a pair, `p1`–`p6`); `short` / `endgame`: each player
 * gets their own random 5 / 1 cards of the standard deck.
 */
export const DECKS = ['standard', 'knights', 'short', 'endgame'];
/**
 * Host's game option: the idle limit, in minutes (0 = off). A human the game has been waiting on
 * this long (their turn, a card, a war roll, the Beginner Mode screens) forfeits and watches
 * instead. See `Game.bootIdle`. The hour-long limits (1 h, 12 h, 24 h) are for slow games played
 * a turn at a time, with turn alerts bringing players back.
 */
export const IDLE_LIMITS = [0, 1440, 720, 60, 30, 10, 5, 3, 1];
/** Clients show an idle player's countdown once this little of their idle limit is left. */
export const IDLE_WARNING_MS = 30_000;
export const CHAT_MAX_LENGTH = 300;
/**
 * Quick Play: a lobby it opens fills with Smart bots up to `QUICK_FILL_SEATS` and starts this long
 * after it opened, unless another human has joined by then.
 */
export const QUICK_FILL_MS = 30_000;
/** Allow Bot Collusion: a human has this long to answer a deal (an offer, or a counter to theirs); then it is declined. */
export const DEAL_ANSWER_MS = 20_000;
export const QUICK_FILL_SEATS = 4;
/** Quick reactions a player or watcher can send to the table; text is `emote.<id>`. */
export const EMOTES = ['nice', 'haha', 'wow', 'oops', 'threat', 'dice', 'please', 'gg'];
/** How long an emote shows over its sender's seat. */
export const EMOTE_SHOW_MS = 3000;
/** Tester feedback reports (POST /api/feedback). */
export const FEEDBACK_CATEGORIES = ['bug', 'suggestion', 'other'];
export const FEEDBACK_MAX_LENGTH = 4000;
export const FEEDBACK_MAX_IMAGES = 4;
/** Per image, as a base64 data URL (about 3 MB decoded). Clients downscale before sending. */
export const FEEDBACK_IMAGE_MAX_CHARS = 4_000_000;
/** The game snapshot, serialized. */
export const FEEDBACK_SNAPSHOT_MAX_CHARS = 1_000_000;
export const DISPLAY_NAME_MAX_LENGTH = 24;
/** Voice chat: longest session description relayed (audio-only ones are a few KB). */
export const VOICE_SDP_MAX_CHARS = 12_000;
/** Player avatars (PUT /api/me/avatar): built-in defaults 1..AVATAR_DEFAULTS, or an upload. */
export const AVATAR_DEFAULTS = 8;
/** Largest upload, decoded. Clients also refuse a picked file bigger than this. */
export const AVATAR_MAX_BYTES = 1_048_576;
/** Clients centre-crop an upload to a square this many pixels wide before sending. */
export const AVATAR_SIZE_PX = 256;
//# sourceMappingURL=constants.js.map